// Loads Umbraco Forms' date picker assets (moment, pikaday + its CSS, datepicker.init) on the
// first focus/tap of a date field instead of during page load. BlockForms.cshtml moves their URLs
// out of the rendered form into #lazy-datepicker-scripts (see Html/LazyDatePickerScripts.cs).
// datepicker.init.min.js initializes Pikaday on every .datepickerfield as soon as it runs when the
// document has already loaded, so all that's left afterwards is reopening the field the visitor
// just interacted with - that focus/tap happened before Pikaday existed.

const SCRIPTS_ELEMENT_ID = "lazy-datepicker-scripts";
const FIELD_SELECTOR = ".datepickerfield";

const isStylesheet = (url: string): boolean => new URL(url, document.baseURI).pathname.endsWith(".css");

function loadAsset(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const element = isStylesheet(url) ? document.createElement("link") : document.createElement("script");
        if (element instanceof HTMLLinkElement) {
            element.rel = "stylesheet";
            element.href = url;
        } else {
            element.src = url;
        }
        element.addEventListener("load", () => resolve(), { once: true });
        element.addEventListener("error", () => {
            // Removed so a retry adds a fresh one instead of piling up failed elements.
            element.remove();
            reject(new Error(`Failed to load ${url}`));
        }, { once: true });
        document.head.append(element);
    });
}

// Scripts in order, not parallel: pikaday needs moment, and datepicker.init needs both. The
// stylesheet loads alongside without being waited on - a failed (cosmetic) stylesheet mustn't
// stop the scripts.
async function loadAll(urls: string[], loaded: Set<string>): Promise<void> {
    for (const url of urls.filter(isStylesheet)) {
        if (!loaded.has(url)) {
            loaded.add(url);
            loadAsset(url).catch((error: unknown) => {
                loaded.delete(url);
                console.error(error);
            });
        }
    }
    for (const url of urls.filter((candidate) => !isStylesheet(candidate))) {
        if (!loaded.has(url)) {
            await loadAsset(url);
            loaded.add(url);
        }
    }
}

export function initLazyDatepicker(): void {
    const scriptsElement = document.getElementById(SCRIPTS_ELEMENT_ID);
    if (!scriptsElement) {
        return;
    }

    const urls = JSON.parse(scriptsElement.textContent ?? "[]") as string[];
    // Survives a failed attempt, so a retry only fetches what's still missing.
    const loaded = new Set<string>();

    const listen = (): void => {
        document.addEventListener("focusin", onInteract);
        document.addEventListener("pointerdown", onInteract);
    };

    function onInteract(event: Event): void {
        if (!(event.target as Element | null)?.closest(FIELD_SELECTOR)) {
            return;
        }

        document.removeEventListener("focusin", onInteract);
        document.removeEventListener("pointerdown", onInteract);

        loadAll(urls, loaded)
            .then(() => {
                // Reopen whichever date field has focus now - the visitor may have moved on to
                // another one (e.g. "to" after "from") while the assets loaded. click() rather
                // than blur()+focus(): Pikaday hides itself on a timer after blur, which closes
                // the picker the refocus just opened.
                document.activeElement?.closest<HTMLElement>(FIELD_SELECTOR)?.click();
            })
            .catch((error: unknown) => {
                // The field still works as a plain text input without the picker. Listen again,
                // so the next interaction retries (e.g. after a brief network error).
                console.error(error);
                listen();
            });
    }

    listen();
}
